import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ObraDashboardMetric {
  id: string;
  nombre: string;
  cliente: {
    nombre: string;
  };
  estado: string;
  horas_estimadas: number;
  horas_consumidas: number;
  coste_estimado: number;
  coste_consumido: number;
  progreso: number;
  jornadas_por_tipo: {
    [key: string]: {
      horas: number;
      cantidad: number;
    };
  };
  sobrecarga: boolean;
  tecnicos_asignados: string[];
}

export interface DashboardMetrics {
  obras_activas: number;
  horas_totales_consumidas: number;
  horas_extra_totales: number;
  obras_sobrecargadas: number;
  distribucion_tipos: {
    [key: string]: {
      horas: number;
      porcentaje: number;
    };
  };
  obras_detalle: ObraDashboardMetric[];
}

export const useObrasDashboardMetrics = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    obras_activas: 0,
    horas_totales_consumidas: 0,
    horas_extra_totales: 0,
    obras_sobrecargadas: 0,
    distribucion_tipos: {},
    obras_detalle: []
  });
  const [loading, setLoading] = useState(true);

  const loadMetrics = async () => {
    try {
      setLoading(true);

      // Obtener obras con información del cliente
      const { data: obras, error: obrasError } = await supabase
        .from('obras')
        .select(`
          id,
          nombre,
          estado,
          horas_estimadas,
          horas_consumidas,
          coste_mano_obra_estimado,
          coste_consumido,
          cliente:clientes(nombre)
        `);

      if (obrasError) throw obrasError;

      // Obtener todas las jornadas con información de obras y técnicos
      const { data: jornadas, error: jornadasError } = await supabase
        .from('obra_jornadas')
        .select(`
          obra_id,
          tipo_trabajo,
          horas_trabajadas,
          es_hora_extra,
          tecnico:perfiles(nombre),
          obra:obras(nombre)
        `);

      if (jornadasError) throw jornadasError;

      // Procesar métricas
      const obrasList = obras || [];
      const jornadasList = jornadas || [];

      // Calcular métricas principales
      const obrasActivas = obrasList.filter(obra => obra.estado === 'activa').length;
      const horasTotales = obrasList.reduce((total, obra) => total + (obra.horas_consumidas || 0), 0);
      const horasExtra = jornadasList
        .filter(jornada => jornada.es_hora_extra)
        .reduce((total, jornada) => total + (jornada.horas_trabajadas || 0), 0);
      const obrasSobrecargadas = obrasList.filter(obra => 
        obra.horas_estimadas > 0 && obra.horas_consumidas > obra.horas_estimadas
      ).length;

      // Distribución por tipos de trabajo
      const distribucionTipos: { [key: string]: { horas: number; porcentaje: number } } = {};
      let horasTotalesPorTipo = 0;

      jornadasList.forEach(jornada => {
        const tipo = jornada.tipo_trabajo;
        const horas = jornada.horas_trabajadas || 0;
        
        if (!distribucionTipos[tipo]) {
          distribucionTipos[tipo] = { horas: 0, porcentaje: 0 };
        }
        distribucionTipos[tipo].horas += horas;
        horasTotalesPorTipo += horas;
      });

      // Calcular porcentajes
      Object.keys(distribucionTipos).forEach(tipo => {
        distribucionTipos[tipo].porcentaje = horasTotalesPorTipo > 0 
          ? (distribucionTipos[tipo].horas / horasTotalesPorTipo) * 100
          : 0;
      });

      // Detalle por obra
      const obrasDetalle: ObraDashboardMetric[] = obrasList.map(obra => {
        const jornadasObra = jornadasList.filter(j => j.obra_id === obra.id);
        
        // Agrupar jornadas por tipo
        const jornadasPorTipo: { [key: string]: { horas: number; cantidad: number } } = {};
        const tecnicosUnicos = new Set<string>();

        jornadasObra.forEach(jornada => {
          const tipo = jornada.tipo_trabajo;
          const horas = jornada.horas_trabajadas || 0;
          
          if (!jornadasPorTipo[tipo]) {
            jornadasPorTipo[tipo] = { horas: 0, cantidad: 0 };
          }
          jornadasPorTipo[tipo].horas += horas;
          jornadasPorTipo[tipo].cantidad += 1;

          if (jornada.tecnico?.nombre) {
            tecnicosUnicos.add(jornada.tecnico.nombre);
          }
        });

        const progreso = obra.horas_estimadas > 0 
          ? Math.min((obra.horas_consumidas / obra.horas_estimadas) * 100, 100)
          : 0;

        return {
          id: obra.id,
          nombre: obra.nombre,
          cliente: obra.cliente || { nombre: 'Sin cliente' },
          estado: obra.estado,
          horas_estimadas: obra.horas_estimadas || 0,
          horas_consumidas: obra.horas_consumidas || 0,
          coste_estimado: Number((obra as any).coste_mano_obra_estimado) || 0,
          coste_consumido: Number((obra as any).coste_consumido) || 0,
          progreso,
          jornadas_por_tipo: jornadasPorTipo,
          sobrecarga: obra.horas_estimadas > 0 && obra.horas_consumidas > obra.horas_estimadas,
          tecnicos_asignados: Array.from(tecnicosUnicos)
        };
      });

      setMetrics({
        obras_activas: obrasActivas,
        horas_totales_consumidas: horasTotales,
        horas_extra_totales: horasExtra,
        obras_sobrecargadas: obrasSobrecargadas,
        distribucion_tipos: distribucionTipos,
        obras_detalle: obrasDetalle
      });

    } catch (error) {
      console.error('Error loading dashboard metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();
  }, []);

  return {
    metrics,
    loading,
    refreshMetrics: loadMetrics
  };
};